import MetricCard from "../MetricCard.jsx"
import {fyLabel, useMetricsQuery, metricBadgeProps} from "./util.jsx"

const currencyFormatter = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
})

const MetricsFinancial = ({filters = {}}) => {
    // const {data, isLoading, error} = useMetricsQuery("financial", filters, {
    //     current: null,
    //     prior: null,
    //     lifetime_value: null,
    //     excluded: 0,
    // })

    const isLoading = false
    const error = null
    const data2027 = {
        "current": {
            "orientations": 145,
            "avg_planned_shifts": 32.3,
            "avg_actual_shifts": 30.3,
            "net_shifts": 281,
            "value": 521634,
            "unpriced": 145
        },
        "prior": {
            "orientations": 791,
            "avg_planned_shifts": 31.9,
            "avg_actual_shifts": 33.1,
            "net_shifts": 904,
            "value": 971655,
            "unpriced": 0
        },
        "lifetime_value": 2622868,
        "excluded": 59
    }

    const data2026 = {
        "current": {
            "orientations": 791,
            "avg_planned_shifts": 33.1,
            "avg_actual_shifts": 31.9,
            "net_shifts": 904,
            "value": 971655,
            "unpriced": 0
        },
        "prior": {
            "orientations": 646,
            "avg_planned_shifts": 33.1,
            "avg_actual_shifts": 33.9,
            "net_shifts": 554,
            "value": 613876,
            "unpriced": 0
        },
        "lifetime_value": 2622868,
        "excluded": 59
    }

    const data2025 = {
        "current": {
            "orientations": 646,
            "avg_planned_shifts": 33.9,
            "avg_actual_shifts": 33.1,
            "net_shifts": 554,
            "value": 613876,
            "unpriced": 0
        },
        "prior": {
            "orientations": 241,
            "avg_planned_shifts": 31.8,
            "avg_actual_shifts": 33.3,
            "net_shifts": 347,
            "value": 420980,
            "unpriced": 0
        },
        "lifetime_value": 2622868,
        "excluded": 59
    }

    const data2024 = {
        "current": {
            "orientations": 241,
            "avg_planned_shifts": 33.3,
            "avg_actual_shifts": 31.8,
            "net_shifts": 347,
            "value": 420980,
            "unpriced": 0
        },
        "prior": {
            "orientations": 196,
            "avg_planned_shifts": 33.9,
            "avg_actual_shifts": 36.1,
            "net_shifts": 439,
            "value": 616356,
            "unpriced": 0
        },
        "lifetime_value": 2622868,
        "excluded": 59
    }

    const data2023 = {
        "current": {
            "orientations": 196,
            "avg_planned_shifts": 36.9,
            "avg_actual_shifts": 33.1,
            "net_shifts": 439,
            "value": 616356,
            "unpriced": 0
        },
        "prior": {
            "orientations": 9,
            "avg_planned_shifts": 34.7,
            "avg_actual_shifts": 34.2,
            "net_shifts": 4,
            "value": null,
            "unpriced": 9
        },
        "lifetime_value": 2622868,
        "excluded": 59
    }

    let data = {};
    if (filters.fiscal_year === 2027) {
        data = data2027;
    } else if (filters.fiscal_year === 2026) {
        data = data2026;
    } else if (filters.fiscal_year === 2025) {
        data = data2025;
    } else if (filters.fiscal_year === 2024) {
        data = data2024;
    } else if (filters.fiscal_year === 2023) {
        data = data2023;
    }

    if (error) return <div className="alert alert-danger">Unable to load financial metrics.</div>

    const {current, prior, lifetime_value: lifetimeValue, excluded} = data
    const fyDisplay = fyLabel(filters.fiscal_year)
    const priorDisplay = fyLabel(filters.fiscal_year - 1)

    const delta = current ? current.avg_actual_shifts - current.avg_planned_shifts : null
    const deltaDisplay = delta == null ? "—" : `${delta > 0 ? "+" : ""}${delta.toFixed(1)}`

    // Fewer shifts than planned is the saving, so a negative net is good.
    const netBadge = current && prior ? metricBadgeProps(current.net_shifts, prior.net_shifts, false) : null
    const valueBadge = current && prior ? metricBadgeProps(current.value, prior.value, true) : null

    return (
        <>
            <h2 className="fw-bold mb-2">{fyDisplay} Shift and Financial Metrics</h2>
            <p className="text-gray-700 fs-6 mb-4">
                {current
                    ? `${current.orientations} orientations completed in this fiscal year.`
                    : "No completed orientations in this fiscal year."}
                {current?.unpriced > 0 &&
                    ` ${current.unpriced} of those have no backfill rate configured for their year.`}
                {excluded > 0 &&
                    ` Across all fiscal years, ${excluded} orientations were excluded with no planned length or shift configuration.`}
            </p>

            <div className="d-flex flex-column flex-lg-row gap-8">
                <MetricCard
                    title="Shifts at Onset vs Actual"
                    subtitle={fyDisplay}
                    value={deltaDisplay}
                    comparison={
                        current
                            ? `Planned ${current.avg_planned_shifts.toFixed(1)} → Actual ${current.avg_actual_shifts.toFixed(1)}`
                            : null
                    }
                    isLoading={isLoading}
                />
                <MetricCard
                    title="Net Shifts Added / Reduced"
                    subtitle={fyDisplay}
                    value={current?.net_shifts ?? "—"}
                    comparison={prior ? `${prior.net_shifts}: ${priorDisplay}` : null}
                    badge={netBadge}
                    isLoading={isLoading}
                />
            </div>
            <div className="d-flex flex-column flex-lg-row gap-8">
                <MetricCard
                    title="Value of Net Shift Reduction"
                    subtitle={fyDisplay}
                    value={current?.value == null ? "—" : currencyFormatter.format(current.value)}
                    comparison={
                        prior?.value != null ? `${currencyFormatter.format(prior.value)}: ${priorDisplay}` : null
                    }
                    badge={valueBadge}
                    isLoading={isLoading}
                />
                <MetricCard
                    title="Lifetime Value of OARS"
                    subtitle="All Time Cumulative"
                    value={lifetimeValue == null ? "—" : currencyFormatter.format(lifetimeValue)}
                    comparison={null}
                    badge={null}
                    isLoading={isLoading}
                />
            </div>
        </>
    )
}

export default MetricsFinancial
